import { useTranslation } from "react-i18next";
import { type Column } from "@/components/meters/meter-table";
import { ReadingProgressBar } from "@/components/mqtt/reading-progress-bar";
import { CopyableCell } from "@/components/shared/copyable-cell";
import { RowDetailsButton } from "@/components/table";
import { Badge } from "@/components/ui/badge";

export type ReadingRowLike = {
	id: number;
	reading_date: string;
	type?: "mqtt" | "advant";
	mqtt_source?: { sn: string } | null;
	advant_source?: { sn: string } | null;
	requested_by: { id: number; email: string } | null;
	completed: boolean;
	meter_readings_count: number;
	cnt_completed: number;
	cnt_error: number;
	cnt_no_communication: number;
	cnt_request_sent: number;
};

function getSourceSn(row: ReadingRowLike): string | null {
	if (row.type === "advant") return row.advant_source?.sn ?? null;
	return row.mqtt_source?.sn ?? row.advant_source?.sn ?? null;
}

export function useReadingsColumns<T extends ReadingRowLike>(opts?: {
	onDetailsClick?: (row: T) => void;
}): Column<T>[] {
	const { t, i18n } = useTranslation();
	const { onDetailsClick } = opts ?? {};

	const columns: Column<T>[] = [
		{
			key: "mqtt_source" as keyof T,
			label: t("mqttReadingsTable.colGatewaySn"),
			sortField: "mqtt_source__sn",
			render: (row) => (
				<CopyableCell
					value={getSourceSn(row)}
					label={t("mqttReadingsTable.colGatewaySn")}
				/>
			),
		},
		{
			key: "requested_by" as keyof T,
			label: t("mqttReadingsTable.colRequestedByUser"),
			render: (row) =>
				row.requested_by ? (
					<span className="text-sm">{row.requested_by.email}</span>
				) : (
					<span className="text-xs text-muted-foreground">
						{t("mqttReadingsTable.automatic")}
					</span>
				),
		},
		{
			key: "completed" as keyof T,
			label: t("mqttReadingsTable.colStatus"),
			render: (row) =>
				row.completed ? (
					<Badge variant="success">
						{t("mqttReadingsTable.statusCompleted")}
					</Badge>
				) : (
					<Badge variant="info">
						{t("mqttReadingsTable.statusInProgress")}
					</Badge>
				),
		},
		{
			key: "meter_readings_count" as keyof T,
			label: t("mqttReadingsTable.colProgress"),
			render: (row) => <ReadingProgressBar reading={row} />,
		},
		{
			key: "reading_date" as keyof T,
			label: t("mqttReadingsTable.colReadingDate"),
			sortField: "reading_date",
			render: (row) =>
				new Date(row.reading_date).toLocaleString(i18n.language, {
					dateStyle: "short",
					timeStyle: "short",
				}),
		},
	];

	if (onDetailsClick) {
		columns.push({
			key: "_actions",
			label: "",
			render: (row) => <RowDetailsButton onClick={() => onDetailsClick(row)} />,
		});
	}

	return columns;
}
